import { useState, useEffect } from 'react'
import { axiosInstance } from '../helpers/axios'
import { IdentifiableNamedObject } from '../models'

export interface PlotData {
    x: string[]
    y: number[]
    total_count: number
    remaining_count: number
    first_month_count: number
}

export interface Additions {
    workspace: PlotData
    unit: PlotData
}

export interface Inspections {
    x: string[]
    y: number[]
    trend: number[]
    text: string[]
}

export interface Schedules {
    x: string[]
    y: number[]
    trend: number[]
    text: string[]
}

export interface ChangeOrders {
    x: string[]
    y: number[]
    trend: number[]
    text: string[]
}

export interface Coordinates {
    x: string[]
    y: number[]
}

export interface Damages {
    x: string[]
    y: number[]
}

export interface Invoices {
    q1: Coordinates
    q2: Coordinates
    q3: Coordinates
    q4: Coordinates
}

export interface UserData {
    x: string[]
    y: number[]
    mom_changes: number[]
    total_users: number
}

export interface VendorData {
    contract: {
        x: string[]
        y: number[]
    }
    in_house: {
        x: string[]
        y: number[]
    }
    total_users: number
}

export interface Users {
    Workspace: UserData
    Tenant: UserData
    Vendor: VendorData
}

export type LeaseAuditStatus =
    | 'REQUESTED'
    | 'SETUP'
    | 'WAITING_FOR_APPROVAL'
    | 'RUNNING_PIPELINE'
    | 'COMPLETED'
    | 'APPROVED'
    | 'REJECTED'

export interface LeaseAudit {
    id: number
    name: string
    status: LeaseAuditStatus
    apartment: IdentifiableNamedObject
    created_date: string
    created_by: IdentifiableNamedObject
    criteria_group: number
    page_count: number
    document_count: number
    data_source: 'UPLOADED' | 'INTEGRATED'
}
export interface EzManageRow {
    apartment_name: string
    tokens: number
}
export type EzManage = EzManageRow[]

export interface LoriKpiData {
    apartment: {
        x: string[]
        y: number[]
    }
    vendor: {
        x: string[]
        y: number[]
    }
}

export interface VendorTransitions {
    apartment: {
        x: string[]
        y: number[]
    }
    vendor: {
        x: string[]
        y: number[]
    }
}

export interface MoveInInspections {
    complete: {
        x: string[]
        y: number[]
    }
    auto: {
        x: string[]
        y: number[]
    }
}

export interface TurnProjectsChart {
    years: number[]
    series: Record<string, number[]>
    cai_avg: Record<string, number[]>
    cai_pooled: Record<string, number[]>
    cai_pass_rate: Record<string, number[]>
    cai_pass_threshold: number
}

export interface TurnProjectDetailRow {
    schedule_id: number
    schedule_name: string
    apartment_id: number
    apartment_name: string
    active: boolean
    year: number | null
    total: number
    primary_method: string
    auto_scheduler_web: number
    lori_step_web: number
    lori_advanced_web: number
    lori_advanced_mobile: number
    bulk_assign_web: number
    walk_scheduler_web: number
    assign: number
    cai_count: number
    cai_rate: number
}

export const useKPI = () => {
    const [additions, setAdditions] = useState<Additions | null>(null)
    const [inspections, setInspections] = useState<Inspections | null>(null)
    const [damages, setDamages] = useState<Damages | null>(null)
    const [
        displayFilteredApartmentStatus,
        setDisplayFilteredApartmentStatus,
    ] = useState<string[]>([])
    const [
        displayFilteredDamageStatus,
        setDisplayFilteredDamageStatus,
    ] = useState<string[]>([])
    const [invoices, setInvoices] = useState<Invoices | null>(null)
    const [schedules, setSchedules] = useState<Schedules | null>(null)
    const [changeOrders, setChangeOrders] = useState<ChangeOrders | null>(null)
    const [users, setUsers] = useState<Users | null>(null)
    const [leaseAudits, setLeaseAudits] = useState<LeaseAudit[]>([])
    const [ezManage, setEzManage] = useState<EzManage | null>(null)
    const [loriKpiData, setLoriKpiData] = useState<LoriKpiData | null>(null)
    const [
        vendorTransitions,
        setVendorTransitions,
    ] = useState<VendorTransitions | null>(null)
    const [
        moveInInspections,
        setMoveInInspections,
    ] = useState<MoveInInspections | null>(null)
    const [
        turnProjectsChart,
        setTurnProjectsChart,
    ] = useState<TurnProjectsChart | null>(null)
    const [turnProjectsDetail, setTurnProjectsDetail] = useState<
        TurnProjectDetailRow[] | null
    >(null)

    const getAdditions = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_additions')
            .then((res) => {
                setAdditions(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getInspections = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_inspections')
            .then((res) => {
                setInspections(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getDamages = async (
        selectedApartmentStatus: string[],
        selectedDamageStatus: string[],
    ) => {
        await axiosInstance
            .get('/stats/kpi/get_damages', {
                params: {
                    apartment_status_filter: selectedApartmentStatus,
                    damage_status_filter: selectedDamageStatus,
                },
            })
            .then((res) => {
                setDamages(res.data)
                setDisplayFilteredApartmentStatus(selectedApartmentStatus)
                setDisplayFilteredDamageStatus(selectedDamageStatus)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getInvoices = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_invoices')
            .then((res) => {
                setInvoices(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getSchedules = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_schedules')
            .then((res) => {
                setSchedules(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getChangeOrders = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_change_orders')
            .then((res) => {
                setChangeOrders(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getUsers = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_users')
            .then((res) => {
                setUsers(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getLoriKpiData = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_lori_messages')
            .then((res) => {
                setLoriKpiData(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getVendorTransitions = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_vendor_workorder_transitions')
            .then((res) => {
                setVendorTransitions(res.data)
            })
    }

    const getMoveInInspections = async () => {
        const res = await axiosInstance
            .get('/stats/kpi/get_move_in_inspections')
            .then((res) => {
                setMoveInInspections(res.data)
            })
    }

    const getEzManage = async () => {
        await axiosInstance
            .get('/stats/kpi/get_token_usage')
            .then((res) => {
                setEzManage(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getTurnProjects = async () => {
        await axiosInstance
            .get('/stats/kpi/get_turn_projects')
            .then((res) => {
                setTurnProjectsChart(res.data.chart)
                setTurnProjectsDetail(res.data.detail)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getTurnProjectsChart = async () => {
        await axiosInstance
            .get('/stats/kpi/get_turn_projects_chart')
            .then((res) => {
                setTurnProjectsChart(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getTurnProjectsDetail = async () => {
        await axiosInstance
            .get('/stats/kpi/get_turn_projects_detail')
            .then((res) => {
                setTurnProjectsDetail(res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    return {
        getAdditions,
        getDamages,
        setDamages,
        getEzManage,
        getInspections,
        getInvoices,
        getSchedules,
        getChangeOrders,
        getUsers,
        getLoriKpiData,
        getVendorTransitions,
        getMoveInInspections,
        getTurnProjects,
        getTurnProjectsChart,
        getTurnProjectsDetail,
        additions,
        inspections,
        damages,
        displayFilteredApartmentStatus,
        displayFilteredDamageStatus,
        invoices,
        schedules,
        changeOrders,
        users,
        leaseAudits,
        loriKpiData,
        vendorTransitions,
        moveInInspections,
        turnProjectsChart,
        turnProjectsDetail,
        ezManage,
    }
}

export type KPIController = ReturnType<typeof useKPI>
